import { createFileRoute } from '@tanstack/react-router';
import { Filter, Plus, Search, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClaimPlayerDialog } from '@/components/features/players/ClaimPlayerDialog';
import { EmptyPlayersState } from '@/components/features/players/EmptyPlayersState';
import { PlayerCardSkeleton } from '@/components/features/players/PlayerCardSkeleton';
import { PlayerFormDialog } from '@/components/features/players/PlayerFormDialog';
import { PlayerMergeHistorySheet } from '@/components/features/players/PlayerMergeHistorySheet';
import { PlayerCard } from '@/components/shared/cards';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/ui/responsive-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SortControl, type SortDirection } from '@/components/ui/sort-control';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  mergePlayers,
  deletePlayer,
  restorePlayer,
  unmergePlayers,
  claimPlayer,
  unclaimPlayer,
} from '@/db/players';
import { replaySessionFromEvents } from '@/db/session-events';
import { getSessionsByStatus } from '@/db/sessions';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useInitialSync } from '@/hooks/useInitialSync';
import { usePlayerMergeTargets } from '@/hooks/usePlayerMergeHistory';
import { usePlayers } from '@/hooks/usePlayers';
import { useTeamPresets } from '@/hooks/useTeamPresets';
import { handleUserFacingError } from '@/lib/error-handler';
import { slideUp, staggerConfig } from '@/lib/motion-config';
import { getErrorMessage } from '@/lib/sync/error-utils';
import { toastError, toastSuccess, toastSuccessWithUndo } from '@/lib/toast-helpers';
import type { Player, PlayerId, Session, TeamId, TeamPreset } from '@/schemas';
import { parsePlayerId } from '@/schemas';
import { useAuthStore } from '@/stores';
import { getActivePlayers } from '@/stores/session-selectors';

export const Route = createFileRoute('/players/')({
  component: PlayersPage,
});

type PlayerSortOption = 'name' | 'nickname' | 'createdAt' | 'updatedAt' | 'status';

const PLAYER_SORT_DEFAULT_DIRECTION: Record<PlayerSortOption, SortDirection> = {
  name: 'asc',
  nickname: 'asc',
  createdAt: 'desc',
  updatedAt: 'desc',
  status: 'asc',
};

async function isPlayerInActiveSession(playerId: PlayerId): Promise<boolean> {
  const activeSessions = await getSessionsByStatus('ongoing');
  const pausedSessions = await getSessionsByStatus('paused');
  const allActiveSessions = [...activeSessions, ...pausedSessions];

  for (const session of allActiveSessions) {
    try {
      const fullSession: Session = await replaySessionFromEvents(session.id);
      const players = getActivePlayers(fullSession);
      if (players.some((p) => p.id === playerId)) {
        return true;
      }
    } catch (error) {
      console.error('Failed to replay session:', error);
    }
  }
  return false;
}

function PlayersPage() {
  const { t } = useTranslation('common');
  const { isSyncing } = useInitialSync();
  const userId = useAuthStore((state) => state.userId);
  // Load all players (active + archived) for filter visibility check based on raw/unfiltered data
  const {
    players,
    isLoading: isLoadingPlayers,
    hasData: hasPlayerData,
    refresh: refreshPlayers,
  } = usePlayers({ includeDeleted: true });
  const { teamPresets } = useTeamPresets();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeamIds, setSelectedTeamIds] = useState<TeamId[]>([]);
  const [statusFilter, setStatusFilter] = useState<'active' | 'deleted' | 'all'>('all');
  const [sortOption, setSortOption] = useState<PlayerSortOption>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    PLAYER_SORT_DEFAULT_DIRECTION.name,
  );
  const [isAdvancedPopoverOpen, setIsAdvancedPopoverOpen] = useState(false);
  const [isAdvancedDrawerOpen, setIsAdvancedDrawerOpen] = useState(false);
  const [showPlayerFormDialog, setShowPlayerFormDialog] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState<Player | undefined>();
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [sourcePlayerId, setSourcePlayerId] = useState<PlayerId | undefined>();
  const [targetPlayerId, setTargetPlayerId] = useState<PlayerId | undefined>();
  const [isMerging, setIsMerging] = useState(false);
  const [mergeError, setMergeError] = useState<string | undefined>();
  const [mergeHistoryPlayer, setMergeHistoryPlayer] = useState<Player | undefined>();
  const [showMergeHistorySheet, setShowMergeHistorySheet] = useState(false);
  const { targetIds: mergeTargetIds } = usePlayerMergeTargets();
  useDocumentTitle(`${t('players')} - ${t('app.name', 'One Score')}`);

  const showInitialSkeleton = useMemo(
    () => (isSyncing || isLoadingPlayers) && !hasPlayerData,
    [isSyncing, isLoadingPlayers, hasPlayerData],
  );

  const targetPlayer = useMemo(
    () => (targetPlayerId ? players.find((player) => player.id === targetPlayerId) : undefined),
    [targetPlayerId, players],
  );

  const availableCombinePlayers = useMemo(
    () => players.filter((player) => player.id !== targetPlayerId && !player.deletedAt),
    [players, targetPlayerId],
  );

  const selectedCombinePlayer = useMemo(
    () => (sourcePlayerId ? players.find((player) => player.id === sourcePlayerId) : undefined),
    [sourcePlayerId, players],
  );

  const playerSortOptions = useMemo(
    () => [
      { value: 'name', label: t('players.filters.sort.name') },
      { value: 'nickname', label: t('players.filters.sort.nickname') },
      { value: 'createdAt', label: t('players.filters.sort.createdAt') },
      { value: 'updatedAt', label: t('players.filters.sort.updatedAt') },
      { value: 'status', label: t('players.filters.sort.status') },
    ],
    [t],
  );

  const sortDirectionLabels = useMemo(
    () => ({
      asc: t('filters.sort.direction.asc'),
      desc: t('filters.sort.direction.desc'),
    }),
    [t],
  );

  const handleSortOptionChange = useCallback((value: string) => {
    const nextValue = (value || 'name') as PlayerSortOption;
    setSortOption(nextValue);
    setSortDirection(PLAYER_SORT_DEFAULT_DIRECTION[nextValue]);
  }, []);

  const handleSortDirectionToggle = useCallback(() => {
    setSortDirection((previous) => (previous === 'asc' ? 'desc' : 'asc'));
  }, []);

  const playerToTeams = useMemo(() => {
    const map = new Map<PlayerId, Set<TeamId>>();
    for (const preset of teamPresets) {
      if (!preset.playerIds || preset.playerIds.length === 0) {
        continue;
      }
      for (const playerId of preset.playerIds) {
        if (!map.has(playerId)) {
          map.set(playerId, new Set<TeamId>());
        }
        map.get(playerId)?.add(preset.id);
      }
    }
    return map;
  }, [teamPresets]);

  const selectableTeamPresets = useMemo(
    () => teamPresets.filter((preset) => preset.playerIds && preset.playerIds.length > 0),
    [teamPresets],
  );

  const isSearchActive = searchQuery.trim().length > 0;
  const hasAdvancedFilters = selectedTeamIds.length > 0;
  const advancedFilterCount = selectedTeamIds.length;
  const hasActiveFilters =
    isSearchActive ||
    hasAdvancedFilters ||
    statusFilter !== 'all' ||
    sortOption !== 'name' ||
    sortDirection !== PLAYER_SORT_DEFAULT_DIRECTION[sortOption];

  const createPlayerMatchers = useMemo(() => {
    const normalizedQuery = searchQuery.toLowerCase().trim();
    const hasTeamFilter = selectedTeamIds.length > 0;

    const matchesStatus = (player: Player) => {
      if (statusFilter === 'all') return true;
      const isDeleted = Boolean(player.deletedAt);
      return statusFilter === 'deleted' ? isDeleted : !isDeleted;
    };

    const matchesTeams = (player: Player) => {
      if (!hasTeamFilter) return true;
      const teamSet = playerToTeams.get(player.id);
      if (!teamSet) {
        return false;
      }
      return selectedTeamIds.some((teamId) => teamSet.has(teamId));
    };

    const matchesQuery = (player: Player) => {
      if (!normalizedQuery) return true;
      const nameMatch = player.name.toLowerCase().includes(normalizedQuery);
      const nicknameMatch = player.nickname?.toLowerCase().includes(normalizedQuery);
      return nameMatch || Boolean(nicknameMatch);
    };

    return { matchesStatus, matchesTeams, matchesQuery };
  }, [searchQuery, statusFilter, selectedTeamIds, playerToTeams]);

  const createPlayerComparator = useMemo(() => {
    const directionMultiplier = sortDirection === 'asc' ? 1 : -1;
    const currentStatusFilter = statusFilter;

    const compareByNickname = (a: Player, b: Player) => {
      const nicknameA = (a.nickname ?? '').toLowerCase();
      const nicknameB = (b.nickname ?? '').toLowerCase();
      return nicknameA.localeCompare(nicknameB) * directionMultiplier;
    };

    const compareByDate = (a: Player, b: Player, field: 'createdAt' | 'updatedAt') => {
      const difference = a[field] - b[field];
      return difference * directionMultiplier;
    };

    const compareByStatus = (a: Player, b: Player) => {
      const aDeleted = Number(Boolean(a.deletedAt));
      const bDeleted = Number(Boolean(b.deletedAt));
      if (aDeleted !== bDeleted) {
        return (aDeleted - bDeleted) * directionMultiplier;
      }
      return a.name.localeCompare(b.name) * directionMultiplier;
    };

    const compareByName = (a: Player, b: Player) => {
      const aDeleted = Number(Boolean(a.deletedAt));
      const bDeleted = Number(Boolean(b.deletedAt));
      if (currentStatusFilter === 'all' && aDeleted !== bDeleted) {
        return aDeleted - bDeleted;
      }
      return a.name.localeCompare(b.name) * directionMultiplier;
    };

    return (a: Player, b: Player) => {
      switch (sortOption) {
        case 'nickname': {
          return compareByNickname(a, b);
        }
        case 'createdAt': {
          return compareByDate(a, b, 'createdAt');
        }
        case 'updatedAt': {
          return compareByDate(a, b, 'updatedAt');
        }
        case 'status': {
          return compareByStatus(a, b);
        }
        default: {
          return compareByName(a, b);
        }
      }
    };
  }, [sortOption, sortDirection, statusFilter]);

  const filteredPlayers = useMemo(() => {
    return players
      .filter(
        (player) =>
          createPlayerMatchers.matchesQuery(player) &&
          createPlayerMatchers.matchesStatus(player) &&
          createPlayerMatchers.matchesTeams(player),
      )
      .toSorted(createPlayerComparator);
  }, [players, createPlayerMatchers, createPlayerComparator]);

  const selectedTeamPresets = useMemo(() => {
    const presets: TeamPreset[] = [];
    for (const teamId of selectedTeamIds) {
      const preset = teamPresets.find((item) => item.id === teamId);
      if (preset) {
        presets.push(preset);
      }
    }
    return presets;
  }, [selectedTeamIds, teamPresets]);

  const handleTeamToggle = useCallback((teamId: TeamId, checked: boolean) => {
    setSelectedTeamIds((prev) => {
      if (checked) {
        if (prev.includes(teamId)) {
          return prev;
        }
        return [...prev, teamId];
      }
      return prev.filter((id) => id !== teamId);
    });
  }, []);

  const renderAdvancedFilters = useCallback(
    () => (
      <div className="space-y-4">
        <div className="space-y-1.5">
          <p className="text-sm font-medium">{t('players.filters.team.heading')}</p>
          <p className="text-xs text-muted-foreground">{t('players.filters.team.description')}</p>
        </div>
        <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
          {selectableTeamPresets.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              {t('players.filters.team.empty')}
            </p>
          ) : (
            selectableTeamPresets.map((preset) => {
              const controlId = `players-filter-team-${preset.id}`;
              const presetName = preset.name || t('players.filters.team.unnamed');
              return (
                <div
                  key={preset.id}
                  className="flex items-start gap-3 rounded-md border bg-muted/30 px-3 py-2"
                >
                  <Checkbox
                    id={controlId}
                    checked={selectedTeamIds.includes(preset.id)}
                    onCheckedChange={(checked) => handleTeamToggle(preset.id, checked === true)}
                    aria-label={presetName}
                  />
                  <div className="flex flex-1 flex-col">
                    <label htmlFor={controlId} className="text-sm font-medium leading-tight">
                      {presetName}
                    </label>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    ),
    [selectableTeamPresets, selectedTeamIds, handleTeamToggle, t],
  );

  const handleCreateClick = useCallback(() => {
    setEditingPlayer(undefined);
    setShowPlayerFormDialog(true);
  }, []);

  const handleEditClick = useCallback((player: Player) => {
    setEditingPlayer(player);
    setShowPlayerFormDialog(true);
  }, []);

  const handleMergeClick = useCallback((player: Player) => {
    setTargetPlayerId(player.id);
    setSourcePlayerId(undefined);
    setMergeError(undefined);
    setShowMergeDialog(true);
  }, []);

  const handleViewMergeHistory = useCallback((playerToInspect: Player) => {
    setMergeHistoryPlayer(playerToInspect);
    setShowMergeHistorySheet(true);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedTeamIds([]);
    setStatusFilter('all');
    setSortOption('name');
    setSortDirection(PLAYER_SORT_DEFAULT_DIRECTION.name);
    setIsAdvancedPopoverOpen(false);
    setIsAdvancedDrawerOpen(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (player: Player) => {
      const isInActive = await isPlayerInActiveSession(player.id);
      if (isInActive) {
        throw new Error(t('players.deleteConfirm.activeSession'));
      }

      await deletePlayer(player.id);
      await refreshPlayers();
    },
    [t, refreshPlayers],
  );

  const handleRestoreClick = useCallback(
    async (player: Player) => {
      await restorePlayer(player.id);
      await refreshPlayers();
    },
    [refreshPlayers],
  );

  const [claimPlayerDialogOpen, setClaimPlayerDialogOpen] = useState(false);
  const [claimPlayerDialogPlayer, setClaimPlayerDialogPlayer] = useState<Player | undefined>();

  const handleClaimClick = useCallback(
    (player: Player): Promise<void> => {
      // Defensive check - button should only be visible when signed in
      if (!userId) {
        toastError(t('players.claimRequiresAuth', 'You must be signed in to claim a player'));
        return Promise.resolve();
      }

      // Open claim dialog to allow handle selection
      setClaimPlayerDialogPlayer(player);
      setClaimPlayerDialogOpen(true);
      return Promise.resolve();
    },
    [userId, t],
  );

  const handleClaimConfirm = useCallback(
    async (handle?: string) => {
      if (!claimPlayerDialogPlayer || !userId) {
        return;
      }

      try {
        // Reserve handle if provided
        if (handle) {
          const { reserveHandle } = await import('@/lib/supabase/handles');
          const { clearHandleRelease } = await import('@/lib/supabase/handle-releases');
          try {
            await reserveHandle(handle, claimPlayerDialogPlayer.id);
            // Clear release record if this is a reclaim
            clearHandleRelease(handle, claimPlayerDialogPlayer.id);
          } catch (error) {
            // If handle reservation fails, still continue with claim but don't set handle
            console.warn('Failed to reserve handle during claim:', error);
            const errorMessage = handleUserFacingError(
              error,
              t('players.claimHandleError', 'Failed to reserve handle'),
            );
            toastError(errorMessage);
            // Claim without handle
            await claimPlayer(claimPlayerDialogPlayer.id, userId);
            await refreshPlayers();
            toastSuccess(t('players.claimSuccess', 'Player claimed'));
            return;
          }
        }

        await claimPlayer(claimPlayerDialogPlayer.id, userId, handle);
        await refreshPlayers();
        toastSuccess(t('players.claimSuccess', 'Player claimed'));
      } catch (error) {
        const errorMessage = handleUserFacingError(
          error,
          t('players.claimError', 'Failed to claim player'),
        );
        toastError(errorMessage);
        // Re-throw so ClaimPlayerDialog can update its loading state
        throw error;
      }
    },
    [claimPlayerDialogPlayer, userId, t, refreshPlayers],
  );

  const handleUnclaimClick = useCallback(
    async (player: Player) => {
      try {
        await unclaimPlayer(player.id);
        await refreshPlayers();
        toastSuccess(t('players.unclaimSuccess', 'Player unclaimed'));
      } catch (error) {
        // Only show error if we haven't already shown success
        // This prevents showing both success and error toasts
        const errorMessage = handleUserFacingError(
          error,
          t('players.unclaimError', 'Failed to unclaim player'),
          { operation: 'unclaimPlayer', playerId: player.id },
        );
        toastError(errorMessage);
        // Re-throw so PlayerCard can update its loading state
        throw error;
      }
    },
    [t, refreshPlayers],
  );

  const handleMergeConfirm = async () => {
    if (!sourcePlayerId || !targetPlayerId) {
      setMergeError(t('players.mergeMissingSelection'));
      return;
    }

    setIsMerging(true);
    setMergeError(undefined);

    const keptPlayerName = targetPlayer?.name ?? t('players.mergeKeepFallback');
    const combinedPlayerName = selectedCombinePlayer?.name;

    try {
      const mergeEventId = await mergePlayers(sourcePlayerId, targetPlayerId);
      setShowMergeDialog(false);
      setSourcePlayerId(undefined);
      setTargetPlayerId(undefined);
      await refreshPlayers();
      toastSuccessWithUndo(
        t('players.mergeSuccess', {
          targetName: keptPlayerName,
          sourceName: combinedPlayerName ?? t('players.mergeSelectFallback'),
        }),
        async () => {
          try {
            await unmergePlayers(mergeEventId);
            await refreshPlayers();
            toastSuccess(t('players.undoMergeSuccess'));
          } catch (error: unknown) {
            console.error('Failed to undo merge', error);
            toastError(t('players.undoMergeError'));
          }
        },
        {
          undoLabel: t('players.undoMergeAction'),
        },
      );
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error, t('players.mergeGenericError'));
      setMergeError(errorMessage);
    } finally {
      setIsMerging(false);
    }
  };

  const handleMergeCancel = () => {
    setShowMergeDialog(false);
    setSourcePlayerId(undefined);
    setTargetPlayerId(undefined);
    setMergeError(undefined);
  };

  useEffect(() => {
    if (!mergeHistoryPlayer) {
      return;
    }
    if (!showMergeHistorySheet) {
      return;
    }

    if (!mergeTargetIds.has(mergeHistoryPlayer.id)) {
      setShowMergeHistorySheet(false);
      setMergeHistoryPlayer(undefined);
    }
  }, [mergeHistoryPlayer, mergeTargetIds, showMergeHistorySheet]);

  return (
    <div className="w-full max-w-6xl space-y-6 px-4 mx-auto">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('players')}</h1>
          <p className="text-muted-foreground">{t('players.description')}</p>
        </div>
        {!showInitialSkeleton && players.length > 0 && (
          <div className="w-full sm:w-auto">
            <Button
              data-testid="create-player-button"
              className="w-full sm:w-auto"
              onClick={handleCreateClick}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('players.addPlayer')}
            </Button>
          </div>
        )}
      </div>

      {/* Filters - visibility based on all players (active + archived), not just filtered */}
      {!showInitialSkeleton && hasPlayerData && players.length >= 3 && (
        <div className="space-y-3 rounded-lg bg-card shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full min-w-0 flex-1 sm:max-w-sm sm:min-w-0">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={
                  !showInitialSkeleton && players.length > 0
                    ? (() => {
                        if (hasActiveFilters) {
                          return t('players.searchWithCountFiltered', {
                            count: filteredPlayers.length,
                            filtered: filteredPlayers.length,
                            total: players.length,
                          });
                        }
                        return t('players.searchWithCount', { count: players.length });
                      })()
                    : t('players.search')
                }
                aria-label={t('players.search')}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="h-10 pl-9 min-w-0"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap sm:flex-shrink-0">
              <Tabs
                value={statusFilter}
                onValueChange={(value) =>
                  setStatusFilter((value as typeof statusFilter | '') || 'all')
                }
                className="w-full sm:w-auto sm:flex-none sm:flex-shrink-0"
                aria-label={t('players.filters.status.label')}
              >
                <TabsList className="flex h-10 w-full flex-wrap gap-1 sm:w-auto">
                  <TabsTrigger value="all">{t('players.filters.status.all')}</TabsTrigger>
                  <TabsTrigger value="active">{t('players.filters.status.active')}</TabsTrigger>
                  <TabsTrigger value="deleted">{t('players.filters.status.deleted')}</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="flex w-full items-center gap-2 sm:w-auto sm:flex-none">
                <div className="w-full sm:w-10 sm:min-w-10 sm:flex-shrink-0">
                  <SortControl
                    label={t('players.filters.sort.label')}
                    value={sortOption}
                    direction={sortDirection}
                    options={playerSortOptions}
                    onValueChange={handleSortOptionChange}
                    onDirectionToggle={handleSortDirectionToggle}
                    directionLabels={sortDirectionLabels}
                    triggerClassName="h-10 w-full sm:w-10 sm:min-w-10 flex-shrink-0"
                  />
                </div>

                <div className="hidden md:block flex-shrink-0 w-10 min-w-10">
                  <Popover open={isAdvancedPopoverOpen} onOpenChange={setIsAdvancedPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant={hasAdvancedFilters ? 'secondary' : 'outline'}
                        size="icon"
                        className="h-10 w-10 min-w-10 flex-shrink-0"
                        aria-label={t('players.filters.more')}
                      >
                        <Filter className="h-4 w-4" />
                        {advancedFilterCount > 0 && (
                          <Badge
                            variant="secondary"
                            className="pointer-events-none absolute -right-2 -top-2 h-5 min-w-[1.25rem] rounded-full px-1 text-[10px]"
                          >
                            {advancedFilterCount}
                          </Badge>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] p-0" align="end">
                      <div className="space-y-4 p-4">
                        {renderAdvancedFilters()}
                        <div className="flex items-center justify-between border-t pt-3">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setSelectedTeamIds([])}
                            disabled={!hasAdvancedFilters}
                          >
                            {t('players.filters.team.clear')}
                          </Button>
                          <PopoverClose asChild>
                            <Button type="button" size="sm">
                              {t('players.filters.done')}
                            </Button>
                          </PopoverClose>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="md:hidden w-full sm:w-10 sm:min-w-10 sm:flex-shrink-0">
                  <Drawer open={isAdvancedDrawerOpen} onOpenChange={setIsAdvancedDrawerOpen}>
                    <DrawerTrigger asChild>
                      <Button
                        type="button"
                        variant={hasAdvancedFilters ? 'secondary' : 'outline'}
                        className="relative flex h-10 w-full sm:w-10 sm:min-w-10 items-center justify-center p-0 flex-shrink-0"
                        aria-label={t('players.filters.more')}
                      >
                        <Filter className="h-4 w-4" />
                        {advancedFilterCount > 0 && (
                          <Badge
                            variant="secondary"
                            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 translate-x-1/2 rounded-full px-2 py-0 text-xs"
                          >
                            {advancedFilterCount}
                          </Badge>
                        )}
                      </Button>
                    </DrawerTrigger>
                    <DrawerContent className="max-h-[85vh]">
                      <DrawerHeader>
                        <DrawerTitle>{t('players.filters.advancedTitle')}</DrawerTitle>
                        <DrawerDescription>
                          {t('players.filters.advancedDescription')}
                        </DrawerDescription>
                      </DrawerHeader>
                      <div className="space-y-4 px-4 pb-4">{renderAdvancedFilters()}</div>
                      <DrawerFooter>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedTeamIds([]);
                          }}
                          disabled={!hasAdvancedFilters}
                        >
                          {t('players.filters.team.clear')}
                        </Button>
                        <Button
                          onClick={() => {
                            setIsAdvancedDrawerOpen(false);
                          }}
                        >
                          {t('players.filters.apply')}
                        </Button>
                      </DrawerFooter>
                    </DrawerContent>
                  </Drawer>
                </div>
              </div>
            </div>
          </div>

          {(statusFilter !== 'all' || hasAdvancedFilters || isSearchActive) && (
            <div className="flex flex-wrap items-center gap-2">
              {isSearchActive && (
                <Badge variant="outline">
                  {t('players.filters.search.badge', { value: searchQuery.trim() })}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-1 h-4 w-4 p-0"
                    onClick={() => setSearchQuery('')}
                    aria-label={t('players.filters.search.clear')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {statusFilter !== 'all' && (
                <Badge variant="secondary">
                  {t(`players.filters.status.badge.${statusFilter}`)}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-2 h-4 w-4 p-0"
                    onClick={() => setStatusFilter('all')}
                    aria-label={t('players.filters.status.clear')}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              )}
              {selectedTeamPresets.map((preset) => (
                <Badge key={preset.id} variant="outline" className="gap-1">
                  {preset.name || t('players.filters.team.unnamed')}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="ml-1 h-4 w-4 p-0"
                    onClick={() => handleTeamToggle(preset.id, false)}
                    aria-label={t('players.filters.team.remove', {
                      team: preset.name || t('players.filters.team.unnamed'),
                    })}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  {t('players.filters.clear')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {mergeError && (
        <Card className="border-destructive bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{mergeError}</p>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {showInitialSkeleton && (
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <PlayerCardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* Player Form Dialog */}
      <PlayerFormDialog
        open={showPlayerFormDialog}
        onOpenChange={setShowPlayerFormDialog}
        player={editingPlayer}
        onSuccess={() => {
          void refreshPlayers();
        }}
        onDelete={async (playerToDelete: Player) => {
          const isInActive = await isPlayerInActiveSession(playerToDelete.id);
          if (isInActive) {
            throw new Error(t('players.deleteConfirm.activeSession'));
          }

          await deletePlayer(playerToDelete.id);
          await refreshPlayers();
          return true;
        }}
      />

      {/* Merge Dialog */}
      <ResponsiveDialog open={showMergeDialog} onOpenChange={setShowMergeDialog}>
        <ResponsiveDialogContent className="sm:max-w-lg">
          <div className="space-y-6 px-4 sm:px-0">
            <ResponsiveDialogHeader className="space-y-2 text-left">
              <ResponsiveDialogTitle>{t('players.mergePlayers')}</ResponsiveDialogTitle>
              <ResponsiveDialogDescription>
                {targetPlayer
                  ? t('players.mergeDescriptionTarget', { playerName: targetPlayer.name })
                  : t('players.mergeDescription')}
              </ResponsiveDialogDescription>
            </ResponsiveDialogHeader>
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/20 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {t('players.mergeKeepLabel')}
                </p>
                <p className="mt-2 text-lg font-semibold leading-6">
                  {targetPlayer?.name ?? t('players.mergeKeepFallback')}
                </p>
                <p className="mt-2 text-sm text-muted-foreground">{t('players.mergeKeepHelper')}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium" htmlFor="merge-player-combine">
                  {t('players.mergeCombineLabel')}
                </label>
                {availableCombinePlayers.length === 0 ? (
                  <p className="text-sm italic text-muted-foreground">
                    {t('players.noTargetPlayers')}
                  </p>
                ) : (
                  <>
                    <Select
                      value={sourcePlayerId ? String(sourcePlayerId) : ''}
                      onValueChange={(value) => {
                        if (value) {
                          setSourcePlayerId(parsePlayerId(value));
                        } else {
                          setSourcePlayerId(undefined);
                        }
                      }}
                      disabled={isMerging}
                    >
                      <SelectTrigger
                        id="merge-player-combine"
                        aria-label={t('players.mergeCombineLabel')}
                      >
                        <SelectValue placeholder={t('players.mergeSelectCombine')} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableCombinePlayers.map((player) => (
                          <SelectItem key={player.id} value={String(player.id)}>
                            {player.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      {selectedCombinePlayer
                        ? t('players.mergeCombineSelected', {
                            sourceName: selectedCombinePlayer.name,
                            targetName: targetPlayer?.name ?? t('players.mergeKeepFallback'),
                          })
                        : t('players.mergeCombineHelper')}
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <Button variant="outline" onClick={handleMergeCancel} disabled={isMerging}>
              {t('cancel')}
            </Button>
            <Button
              onClick={() => {
                void handleMergeConfirm();
              }}
              disabled={isMerging || !sourcePlayerId || availableCombinePlayers.length === 0}
            >
              {isMerging ? t('players.merging') : t('players.merge')}
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      {/* Empty State */}
      {!showInitialSkeleton && players.length === 0 && (
        <EmptyPlayersState onCreateClick={handleCreateClick} />
      )}

      {/* Players Grid */}
      {!showInitialSkeleton && filteredPlayers.length > 0 && (
        <motion.div
          data-testid="player-list"
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          variants={slideUp}
          initial="initial"
          animate="animate"
          transition={staggerConfig.container}
        >
          <AnimatePresence mode="popLayout">
            {filteredPlayers.map((player) => {
              const hasAvailableTargets = players.some(
                (otherPlayer) => otherPlayer.id !== player.id && !otherPlayer.deletedAt,
              );

              const playerTeamIds = playerToTeams.get(player.id);

              return (
                <motion.div
                  key={player.id}
                  layout
                  layoutId={`player-card-${player.id}`}
                  variants={slideUp}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  <PlayerCard
                    data-testid={`player-card-${player.id}`}
                    player={player}
                    onEdit={handleEditClick}
                    onDelete={player.deletedAt ? undefined : handleDeleteClick}
                    onRestore={player.deletedAt ? handleRestoreClick : undefined}
                    onMerge={hasAvailableTargets ? handleMergeClick : undefined}
                    hasMergeHistory={mergeTargetIds.has(player.id)}
                    onViewMergeHistory={handleViewMergeHistory}
                    onClaim={handleClaimClick}
                    onUnclaim={handleUnclaimClick}
                    teamPresets={teamPresets}
                    playerTeamIds={playerTeamIds}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      )}

      {/* No Results from Search */}
      {!showInitialSkeleton &&
        players.length > 0 &&
        filteredPlayers.length === 0 &&
        hasActiveFilters && (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Search className="h-8 w-8" />
              </EmptyMedia>
              <EmptyTitle>{t('players.noResults', 'No players match your filters')}</EmptyTitle>
              <EmptyDescription>
                {t(
                  'players.noResultsDescription',
                  "Try adjusting your filters or search terms to find the player you're looking for.",
                )}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" onClick={handleClearFilters}>
                {t('players.filters.clear')}
              </Button>
            </EmptyContent>
          </Empty>
        )}

      <PlayerMergeHistorySheet
        open={showMergeHistorySheet && Boolean(mergeHistoryPlayer)}
        onOpenChange={(open) => {
          setShowMergeHistorySheet(open);
          if (!open) {
            setMergeHistoryPlayer(undefined);
          }
        }}
        player={mergeHistoryPlayer}
      />

      {claimPlayerDialogPlayer && userId && (
        <ClaimPlayerDialog
          open={claimPlayerDialogOpen}
          onOpenChange={(open) => {
            setClaimPlayerDialogOpen(open);
            if (!open) {
              setClaimPlayerDialogPlayer(undefined);
            }
          }}
          player={claimPlayerDialogPlayer}
          userId={userId}
          onConfirm={handleClaimConfirm}
        />
      )}
    </div>
  );
}
